import Vue from 'vue';
import Component from 'vue-class-component';
import { Action, Getter } from 'vuex-class';
import { ORDERS_LOAD } from '@/apps/cart/store/types';
import helpers from '@/lib/helpers';
import ImgCache from 'imgcache.js';

/**
 * Provide base functionality for start and stop loading
 */
@Component
export default class LoadMenuMixin extends Vue {
  @Getter items;
  @Action(ORDERS_LOAD) loadOrders;

  loading = false;

  get menuIsLoading() {
    return this.loading;
  }

  get isCordovaApp() {
    return 'cordova' in window;
  }

  get isBrowserSupportCacheApi() {
    // eslint-disable-next-line no-restricted-globals
    return 'caches' in self;
  }

  async created() {
    try {
      this.loading = true;
      await this.loadOrders();
      await this.preloadImages();
    } catch (error) {
      this.$router.push({ name: 'main' });
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Init image cache.
   * @return {Promise<unknown>}
   */
  initImageCache() {
    ImgCache.options.debug = false;
    ImgCache.options.chromeQuota = 50 * 1024 * 1024;

    if (this.isCordovaApp && cordova.file) {
      ImgCache.options.cordovaFilesystemRoot = cordova.file.dataDirectory;
    }

    return new Promise((resolve, reject) => {
      ImgCache.init(() => resolve(), () => reject(new Error('ImgCache init: error! Check the log for errors')));
    });
  }

  /**
   * Cache image.
   * @param {Image} image
   */
  cacheImage(image) {
    ImgCache.isCached(image.src, (path, success) => {
      if (success) {
        ImgCache.useCachedFile(image);
      } else {
        ImgCache.cacheFile(image.src, () => {
          ImgCache.useCachedFile(image);
        });
      }
    });
  }

  /**
   * Preload images to remove display lag in configurator
   *
   * While images are loading, the spinner will be displayed in the
   * configurator.
   */
  async preloadImages() {
    if (this.isCordovaApp && !this.isBrowserSupportCacheApi) {
      try {
        await this.initImageCache();
      } catch (e) {
        console.warn(e);
      }
    }

    Object.values(this.items).forEach((item) => {
      item.images.forEach((image) => {
        const preloadImage = new Image();
        const eventName = `preloadImage ${image.url}`;

        this.$wait.start(eventName);

        /** Handler for finish loading */
        const endHandler = () => { this.$wait.end(eventName); };

        preloadImage.onload = endHandler;
        preloadImage.onerror = endHandler;

        preloadImage.src = helpers.getImagePath(image.url);

        if (this.isCordovaApp && !this.isBrowserSupportCacheApi) {
          this.cacheImage(preloadImage);
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/lib/mixins/load-menu-mixin.js